import { create } from 'zustand';
import { useSourcingRunStore } from './appSourcingRunStore';
import {
  FrontendRestaurantSectionPlatform,
  fetchPlatformsBySectionIds,
} from '../services/restaurant/restaurantPlatformService';
import {
  FrontendRestaurant,
  fetchRestaurants,
  ReportSettings,
  AccountProductFavoritesScope,
  fetchAccountProductFavorites,
  setAccountProductFavorite,
} from '../services/restaurant/restaurantService';
import {
  FrontendRestaurantSection,
  fetchSectionsByRestaurantIds,
} from '../services/restaurant/restaurantSectionService';
import {
  FrontendRestaurantRevenueSource,
  fetchRevenueSourcesByRestaurantIds
} from '../services/restaurant/restaurantRevenueSourceService';



/**
 * The section an ordering account sits in, plus that account's own `combinedOrderingAccount` flag.
 * Per-platform, NOT per-section: a restaurant can keep separate food/drinks accounts at one supplier
 * and a single combined account at another, so two platforms in the same section can disagree.
 * Every food/drinks split reads it from here via `order.restaurantSection`.
 * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §5
 */
export type PlatformSectionContext = FrontendRestaurantSection & { combinedOrderingAccount: boolean };

interface AccountFavoritesState {
  context: string;
  generation: number;
  keys: string[];
  loaded: boolean;
  loading: boolean;
  pending: string[];
  error: 'loadError' | 'saveError' | null;
}

const emptyAccountFavorites: AccountFavoritesState = {
  context: '', generation: 0, keys: [], loaded: false, loading: false, pending: [], error: null,
};
const accountFavoritesField = {
  products: 'accountFavorites',
  utilization: 'accountUtilizationFavorites',
} as const;

interface RestaurantState {
  restaurants: FrontendRestaurant[];
  restaurantSections: Record<string, FrontendRestaurantSection[]>;

  restaurantSectionPlatforms: Record<string, FrontendRestaurantSectionPlatform[]>;
  platformToSectionIndex: Record<string, PlatformSectionContext>;

  restaurantRevenueSources: Record<string, FrontendRestaurantRevenueSource[]>;

  // Report settings (cost targets, waste %, tracked dishes) keyed by restaurant.
  // Populated from the restaurants load — consumers read here instead of
  // re-fetching the report-settings endpoint per page.
  reportSettings: Record<string, ReportSettings>;

  accountFavorites: AccountFavoritesState;
  accountUtilizationFavorites: AccountFavoritesState;
  loadAccountFavorites: (context: string, scope?: AccountProductFavoritesScope) => Promise<void>;
  toggleAccountFavorite: (context: string, key: string, scope?: AccountProductFavoritesScope) => Promise<void>;

  isLoading: boolean;
  isDataLoaded: boolean;

  setRestaurantSectionPlatforms: (
    restaurantSectionId: string,
    platforms: FrontendRestaurantSectionPlatform[]
  ) => void;

  setRestaurantRevenueSources: (
    restaurantId: string,
    revenueSources: FrontendRestaurantRevenueSource[]
  ) => void;

  // Update one restaurant's cached report settings (after an admin edits them in
  // ReportSettingsModal) so every consumer reflects it without a refetch.
  setReportSettings: (
    restaurantId: string,
    settings: ReportSettings
  ) => void;

  loadAllData: (forceReload?: boolean) => Promise<void>;
}

export const useRestaurantStore = create<RestaurantState>((set, get) => ({
  restaurants: [],
  restaurantSections: {},

  restaurantSectionPlatforms: {},
  platformToSectionIndex: {},

  restaurantRevenueSources: {},

  reportSettings: {},

  accountFavorites: { ...emptyAccountFavorites },
  accountUtilizationFavorites: { ...emptyAccountFavorites },

  loadAccountFavorites: async (context, scope = 'products') => {
    const field = accountFavoritesField[scope];
    const previous = get()[field];
    if (previous.context === context && (previous.loading || previous.pending.length > 0)) return;
    const generation = previous.generation + 1;
    set({ [field]: {
      context, generation, keys: previous.context === context ? previous.keys : [],
      loaded: previous.context === context && previous.loaded, loading: true, pending: [], error: null,
    } });
    try {
      const keys = await fetchAccountProductFavorites(scope);
      const current = get()[field];
      if (current.context !== context || current.generation !== generation) return;
      set({ [field]: { ...current, keys, loaded: true, loading: false } });
    } catch (error) {
      console.error('Failed to load account product favorites', error);
      const current = get()[field];
      if (current.context !== context || current.generation !== generation) return;
      set({ [field]: { ...current, loading: false, error: 'loadError' } });
    }
  },

  toggleAccountFavorite: async (context, key, scope = 'products') => {
    const field = accountFavoritesField[scope];
    const previous = get()[field];
    if (previous.context !== context || !previous.loaded || previous.loading || previous.pending.includes(key)) return;
    const favorite = !previous.keys.includes(key);
    set({ [field]: { ...previous,
      keys: favorite ? [...previous.keys, key] : previous.keys.filter(k => k !== key),
      pending: [...previous.pending, key], error: null,
    } });
    try {
      await setAccountProductFavorite(key, favorite, scope);
    } catch (error) {
      console.error('Failed to save account product favorite', error);
      const current = get()[field];
      if (current.context !== context || current.generation !== previous.generation) return;
      // Roll back this product only; other report/product changes must survive.
      set({ [field]: { ...current, error: 'saveError',
        keys: favorite ? current.keys.filter(k => k !== key) : [...current.keys, key],
      } });
    } finally {
      const current = get()[field];
      if (current.context === context && current.generation === previous.generation) {
        set({ [field]: { ...current, pending: current.pending.filter(k => k !== key) } });
      }
    }
  },

  isLoading: false,
  isDataLoaded: false,

  setReportSettings: (restaurantId, settings) => {
    set(state => ({
      reportSettings: {
        ...state.reportSettings,
        [restaurantId]: settings,
      },
    }));
  },

  setRestaurantSectionPlatforms: (restaurantSectionId, platforms) => {
    set(state => ({
      restaurantSectionPlatforms: {
        ...state.restaurantSectionPlatforms,
        [restaurantSectionId]: platforms,
      },
    }));
  },

  setRestaurantRevenueSources: (restaurantId, revenueSources) => {
    set(state => ({
      restaurantRevenueSources: {
        ...state.restaurantRevenueSources,
        [restaurantId]: revenueSources,
      },
    }));
  },

  loadAllData: async (forceReload = false) => {
    // Skip if data is already loaded and not forcing reload
    if (get().isDataLoaded && !forceReload) {
      return;
    }
    set(() => ({
      isLoading: true,
    }));

    // 1. Fetch all restaurants
    const restaurants = await fetchRestaurants();
    const restaurantIds = restaurants.map(r => r.id);

    // 2. Batch fetch all sections for all restaurants
    const allSections = await fetchSectionsByRestaurantIds(restaurantIds);
    const sectionIds = allSections.map(s => s.id);

    // 3. Batch fetch platforms and revenue sources in parallel
    const [allPlatforms, allRevenueSources] = await Promise.all([
      fetchPlatformsBySectionIds(sectionIds),
      fetchRevenueSourcesByRestaurantIds(restaurantIds)
    ]);

    // 4. Build the mappings and indexes
    const sectionsMapping: Record<string, FrontendRestaurantSection[]> = {};
    const platformsMapping: Record<string, FrontendRestaurantSectionPlatform[]> = {};
    const platformToSectionMap: Record<string, PlatformSectionContext> = {};
    const revenueSourcesMapping: Record<string, FrontendRestaurantRevenueSource[]> = {};

    // Group sections by restaurant
    allSections.forEach(section => {
      if (!sectionsMapping[section.restaurantId]) {
        sectionsMapping[section.restaurantId] = [];
      }
      sectionsMapping[section.restaurantId].push(section);
    });

    // Group platforms by section and build reverse index
    allPlatforms.forEach(platform => {
      if (!platformsMapping[platform.restaurantSectionId]) {
        platformsMapping[platform.restaurantSectionId] = [];
      }
      platformsMapping[platform.restaurantSectionId].push(platform);

      // Build reverse index: platformId -> section + this account's combined-ordering flag
      const section = allSections.find(s => s.id === platform.restaurantSectionId);
      if (section) {
        platformToSectionMap[platform.id] = {
          ...section,
          combinedOrderingAccount: platform.combinedOrderingAccount,
        };
      }
    });

    // Group revenue sources by restaurant
    allRevenueSources.forEach(revenueSource => {
      if (!revenueSourcesMapping[revenueSource.restaurantId]) {
        revenueSourcesMapping[revenueSource.restaurantId] = [];
      }
      revenueSourcesMapping[revenueSource.restaurantId].push(revenueSource);
    });

    // Report settings ride along in the restaurants payload — key them by id so
    // consumers never have to hit the report-settings endpoint. A restaurant with
    // no settings yet gets an empty default so consumers always have an object.
    const reportSettingsMapping: Record<string, ReportSettings> = {};
    restaurants.forEach(r => {
      reportSettingsMapping[r.id] = r.reportSettings ?? { trackedDishes: { food: [], drinks: [] } };
    });

    set(() => ({
      restaurants,
      restaurantSections: sectionsMapping,
      restaurantSectionPlatforms: platformsMapping,
      platformToSectionIndex: platformToSectionMap,
      restaurantRevenueSources: revenueSourcesMapping,
      reportSettings: reportSettingsMapping,
      isDataLoaded: true,
      isLoading: false,
    }));
  },
}));

// Access-list updates invalidate in-memory sourcing evidence even when its page is unmounted.
useRestaurantStore.subscribe((state, previous) => {
  if (!state.isDataLoaded || !previous.isDataLoaded || state.restaurants === previous.restaurants) return;
  const ids = (restaurants: FrontendRestaurant[]) => restaurants.map(restaurant => restaurant.id).sort().join(',');
  if (ids(state.restaurants) !== ids(previous.restaurants)) useSourcingRunStore.getState().clear();
});
